import { useCallback, useEffect, useState } from 'react';
import type { PlaytestApiKey } from '@playerbase/types';

import {
  fetchPlaytestApiKey,
  rotatePlaytestApiKey,
} from '../../../games/playtest-api-key-data.js';
import { useShell } from '../../../shell/use-shell.js';
import { ApiKeyCard } from './api-key-card.js';
import { EventFiringCard } from './event-firing-card.js';
import { PairingIntegrationCard } from './pairing-integration-card.js';

// Standalone surface for the pairing-side integration: API key + the
// pair-endpoint snippet. Split out from Recruitment so studios can hand
// the integration tab to an engineer without dragging them through the
// Discord-side message config.
export function PlaytestPairingIntegrationPage(): JSX.Element {
  const { activeGame } = useShell();
  const gameId = activeGame?.id ?? null;

  const [meta, setMeta] = useState<PlaytestApiKey | null>(null);
  // The full plaintext key. Set transiently after the rotate RPC returns;
  // cleared as soon as the user dismisses the reveal. The page never
  // re-reads plaintext - if the user navigates away they have to rotate
  // again to see it.
  const [plaintext, setPlaintext] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (gameId === null) return;
    setLoading(true);
    const row = await fetchPlaytestApiKey(gameId);
    setMeta(row);
    setLoading(false);
  }, [gameId]);

  useEffect(() => {
    if (gameId === null) return;
    setPlaintext(null);
    void reload();
  }, [gameId, reload]);

  const handleRotate = useCallback(async () => {
    if (gameId === null) return;
    setRotating(true);
    setError(null);
    const result = await rotatePlaytestApiKey(gameId);
    setRotating(false);
    if (!result.ok || result.data === null) {
      setError(
        result.error === 'not_a_member'
          ? 'You no longer have access to this game.'
          : "Couldn't generate the key right now - try again in a moment.",
      );
      return;
    }
    setPlaintext(result.data.key);
    await reload();
  }, [gameId, reload]);

  const handleDismiss = useCallback(() => {
    setPlaintext(null);
  }, []);

  if (activeGame === null) {
    return <div className="px-2 py-6 text-sm text-text-muted">No game selected.</div>;
  }

  return (
    <div className="flex min-h-full flex-1 flex-col">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-5 px-6 py-6">
      <div className="flex flex-col gap-1">
        <h1 className="font-display text-2xl font-bold text-text">
          Game integration
        </h1>
        <p className="text-sm text-text-muted">
          Two drop-ins for your engineer: a one-time pairing handshake that
          turns a player&rsquo;s pasted code into a durable token, and an
          event-firing call you sprinkle around the moments you set up on
          each playtest flow.
        </p>
      </div>

      <ApiKeyCard
        meta={meta}
        plaintext={plaintext}
        loading={loading}
        rotating={rotating}
        error={error}
        onRotate={handleRotate}
        onDismissReveal={handleDismiss}
      />

      <PairingIntegrationCard plaintext={plaintext} />

      <EventFiringCard meta={meta} plaintext={plaintext} />
      </div>
    </div>
  );
}
